import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Sparkles, User as UserIcon, LogOut, AlertCircle, RefreshCw, RotateCw } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openProCheckout } from "@/lib/paypal-adapter";
import { trackEvent } from "@/lib/analytics";

const STATUS_LABEL = {
  active: { label: "Active", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  past_due: { label: "Payment failed", tone: "text-amber-800 bg-amber-50 border-amber-200" },
  paused: { label: "Paused", tone: "text-neutral-700 bg-neutral-100 border-neutral-200" },
  canceled: { label: "Canceled", tone: "text-neutral-500 bg-neutral-100 border-neutral-200" },
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

export default function Settings() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [billing, setBilling] = useState(null);
  const [config, setConfig] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    try {
      const [bres, cres] = await Promise.all([
        api.get("/billing/me"),
        api.get("/billing/config"),
      ]);
      setBilling(bres.data);
      setConfig(cres.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading]);

  // Poll after returning from PayPal checkout. Auto-reconciles from PayPal
  // if the webhook hasn't fired within a few seconds.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("billing") !== "success" && params.get("billing") !== "pending") return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        // On attempt 3+, ask the backend to pull state directly from PayPal
        // in case the webhook is delayed or the endpoint isn't registered yet.
        if (attempts === 3) {
          try { await api.post("/billing/paypal/reconcile"); } catch { /* fall through */ }
        }
        const { data } = await api.get("/billing/me");
        setBilling(data);
        if (data?.plan === "pro" && data?.has_access) {
          clearInterval(interval);
          // Google Ads: Subscribe conversion (fire once per activation)
          try {
            const key = "ps_conv_fired";
            if (typeof window !== "undefined" && !sessionStorage.getItem(key) && typeof window.gtag === "function") {
              window.gtag("event", "conversion", {
                send_to: "AW-17739393214/uNIICMHu3N4cEL7R5opC",
              });
              sessionStorage.setItem(key, "1");
            }
          } catch { /* gtag not loaded (e.g. adblocker) — ignore */ }
          toast.success("Welcome to Pro. Unlimited remixes are live.");
          navigate("/settings", { replace: true });
        }
      } catch { /* ignore */ }
      if (attempts >= 20) clearInterval(interval);
    }, 1500);
    return () => clearInterval(interval);
  }, [location.search, navigate]);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/billing/paypal/reconcile");
      if (data.reconciled) {
        toast.success("Synced from PayPal. Refreshing…");
        await load();
      } else if (data.reason === "no_customer") {
        toast.error("No PayPal subscription found on record. Did you complete checkout?");
      } else if (data.reason === "no_subscription") {
        toast.error("No subscriptions yet on PayPal.");
      } else {
        toast.error("Nothing to sync.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sync with PayPal");
    } finally {
      setReconciling(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleManage = async () => {
    const ok = window.confirm(
      "Cancel your PostShifted Pro subscription?\n\n" +
      "You keep Pro access until the end of your current billing period. " +
      "You can re-subscribe any time."
    );
    if (!ok) return;
    setPortalLoading(true);
    try {
      await api.post("/billing/paypal/cancel");
      toast.success("Subscription cancelled. You keep Pro until the end of the period.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!config?.configured) {
      navigate("/pricing");
      return;
    }
    setUpgradeLoading(true);
    trackEvent("checkout_started", { source: "settings_page" });
    try {
      await openProCheckout({
        email: user?.email,
        userId: user?.user_id,
        onCompleted: () => {
          toast.success("Payment received. Activating Pro…");
          setTimeout(() => navigate("/settings?billing=success"), 300);
        },
      });
    } catch (err) {
      toast.error(err?.message || "Could not open checkout");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const status = billing?.subscription_status;
  const isPro = billing?.plan === "pro" && billing?.has_access;
  const statusMeta = status ? STATUS_LABEL[status] : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 py-12 lg:px-10">
        <div className="mb-10">
          <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— Account</p>
          <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">Settings</h1>
          <p className="mt-3 text-sm text-neutral-600">
            Manage your PostShifted account, plan, and billing.
          </p>
        </div>

        {/* Profile */}
        <section
          className="mb-8 rounded-none border border-neutral-200 bg-white p-8 shadow-soft"
          data-testid="settings-profile-card"
        >
          <div className="mb-6 flex items-center gap-3">
            <UserIcon className="h-5 w-5 text-coral" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl font-semibold tracking-tight">Profile</h2>
          </div>
          <dl className="space-y-4 text-sm">
            <div className="flex items-baseline justify-between border-b border-neutral-100 pb-3">
              <dt className="text-xs uppercase tracking-widest text-neutral-500">Name</dt>
              <dd data-testid="settings-name" className="font-medium">{user?.name || "—"}</dd>
            </div>
            <div className="flex items-baseline justify-between border-b border-neutral-100 pb-3">
              <dt className="text-xs uppercase tracking-widest text-neutral-500">Email</dt>
              <dd data-testid="settings-email" className="font-medium">{user?.email || "—"}</dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-xs uppercase tracking-widest text-neutral-500">Sign-in method</dt>
              <dd className="font-medium capitalize">{user?.provider || "email"}</dd>
            </div>
          </dl>
        </section>

        {/* Billing */}
        <section
          className="mb-8 rounded-none border border-neutral-200 bg-white p-8 shadow-soft"
          data-testid="settings-billing-card"
        >
          <div className="mb-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-coral" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl font-semibold tracking-tight">Billing</h2>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="settings-billing-refresh"
              className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
              Refresh
            </button>
          </div>

          <div className="mb-6 flex items-baseline justify-between border-b border-neutral-100 pb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500">Current plan</p>
              <p
                data-testid="settings-plan"
                className={`mt-1 font-serif text-3xl font-bold tracking-tight ${
                  isPro ? "text-coral" : "text-neutral-900"
                }`}
              >
                {isPro ? "Pro" : "Free"}
              </p>
            </div>
            {isPro ? (
              <div className="text-right">
                <span className="font-serif text-4xl font-bold">$19</span>
                <div className="text-xs text-neutral-500">/ month</div>
              </div>
            ) : (
              <div className="text-right">
                <p className="text-xs text-neutral-500">5 remixes per month</p>
                <p className="text-xs text-neutral-500">8,000 chars per input</p>
              </div>
            )}
          </div>

          {isPro && (
            <div className="mb-6 space-y-3 text-sm">
              {statusMeta && (
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-neutral-500">Status</span>
                  <span
                    data-testid="settings-subscription-status"
                    className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-medium ${statusMeta.tone}`}
                  >
                    {statusMeta.label}
                  </span>
                </div>
              )}
              {billing?.current_period_end && (
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-neutral-500">
                    {billing?.cancel_at_period_end ? "Ends on" : "Renews on"}
                  </span>
                  <span className="font-medium">{formatDate(billing.current_period_end)}</span>
                </div>
              )}
              {status === "past_due" && (
                <p className="flex items-start gap-2 border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
                  We couldn&apos;t collect your latest payment. Update your card in the portal to
                  keep Pro access.
                </p>
              )}
            </div>
          )}

          {isPro ? (
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleManage}
                disabled={portalLoading}
                data-testid="settings-manage-subscription-btn"
                variant="outline"
                className="h-12 rounded-[3px] border-neutral-300 text-neutral-700 hover:border-neutral-900"
              >
                {portalLoading ? "Cancelling…" : "Cancel subscription"}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/pricing")}
                className="h-12 rounded-[3px] border-neutral-300"
                data-testid="settings-view-pricing"
              >
                View plans
              </Button>
            </div>
          ) : (
            <div className="rounded-none border border-dashed border-coral bg-coral-subtle/40 p-5">
              <p className="font-serif text-lg font-semibold">
                Ready for unlimited?
              </p>
              <p className="mt-1 text-sm text-neutral-700">
                Pro unlocks unlimited remixes, larger inputs (40k chars), and priority generation.
                Start with a <strong>7-day free trial</strong> — you won&apos;t be charged the
                <strong> $19/month</strong> until day 8. Cancel anytime.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  onClick={handleUpgrade}
                  disabled={upgradeLoading}
                  data-testid="settings-upgrade-btn"
                  className="h-12 rounded-[3px] bg-coral text-white hover:bg-coral-hover hover:shadow-coral"
                >
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {upgradeLoading ? "Redirecting to PayPal…" : "Start 7-day free trial"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/pricing")}
                  className="h-12 rounded-[3px] border-neutral-300"
                  data-testid="settings-see-pricing"
                >
                  See what&apos;s included
                </Button>
              </div>
              {config?.configured && (
                <div className="mt-5 flex items-start gap-2 border-t border-dashed border-coral/40 pt-4">
                  <RotateCw className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-500" strokeWidth={1.5} />
                  <div className="flex-1 text-xs text-neutral-600">
                    Already paid but still seeing Free?{" "}
                    <button
                      type="button"
                      onClick={handleReconcile}
                      disabled={reconciling}
                      data-testid="settings-sync-billing"
                      className="font-medium text-coral underline underline-offset-4 hover:text-coral-hover disabled:opacity-50"
                    >
                      {reconciling ? "Syncing…" : "Sync from PayPal"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Danger zone */}
        <section
          className="rounded-none border border-neutral-200 bg-white p-8 shadow-soft"
          data-testid="settings-danger-card"
        >
          <div className="mb-4">
            <h2 className="font-serif text-2xl font-semibold tracking-tight">Session</h2>
            <p className="mt-1 text-xs text-neutral-500">
              Sign out of this browser. Your data stays safe.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={async () => { await logout(); navigate("/", { replace: true }); }}
            data-testid="settings-signout-btn"
            className="h-11 rounded-[3px] border-neutral-300"
          >
            <LogOut className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Sign out
          </Button>
        </section>
      </main>
      <Footer />
    </div>
  );
}
